import { BottomsheetType } from '@/app/constants/bottomsheeTypes';
import { useHashChangeHandler } from '@/app/lib/hooks/useHashChangeHandler';
import { isSuccess } from '@/app/lib/result';
import Dialog from '@ixigo/iui/Dialog';
import Slide, { type SlideProps } from '@ixigo/iui/Slide';
import { Fragment, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { mapEmiPlanToDomain } from '../../domain/payment/mappers';
import usePaymentStore from '../../store';
import Header from '../Header';
import PaymentSectionCard from '../PaymentSectionCard';
import EMIPage from './EMIPage';
import { fetchEmiPlans } from './apis';

const EMISectionContainer = ({ isExpanded }: { isExpanded: boolean }) => {
	const { emiSection, transaction, setEmiOptions, emiOptions } = usePaymentStore(
		useShallow(({ emiSection, transaction, setEmiOptions, emiOptions }) => ({
			emiSection,
			transaction,
			setEmiOptions,
			emiOptions,
		})),
	);
	const [showEMIPage, setShowEMIPage] = useState<boolean>(false);
	const { closeWithHash } = useHashChangeHandler(
		showEMIPage,
		() => setShowEMIPage(false),
		BottomsheetType.EMI_PAGE,
		[`#${BottomsheetType.INDIVIDUAL_OPTIONS_SHEET}`, `#${BottomsheetType.CARD_DOTP}`],
	);

	useEffect(() => {
		if (emiSection?.amount && transaction)
			fetchEmiPlans(emiSection.amount, transaction.orderId).then((emiOptions) => {
				if (isSuccess(emiOptions)) {
					const mappedEmiOptions = emiOptions.data.map((emiOption) => {
						return {
							...emiOption,
							emiInfo: emiOption.emiInfo.map((emiInfo) => mapEmiPlanToDomain(emiInfo)),
						};
					});
					setEmiOptions(mappedEmiOptions);
				} else {
					setEmiOptions([]);
				}
			});
	}, [emiSection, transaction, setEmiOptions]);

	if (!emiSection) return null;

	const { options: savedCards = [], logo, heading, subText, pgFeeText, tag } = emiSection || {};

	return isExpanded ? (
		<EMIPage
			savedCards={savedCards}
			emiOptions={emiOptions}
			isExpanded={isExpanded}
			className="mt-15"
		/>
	) : (
		<Fragment>
			<PaymentSectionCard
				logo={logo}
				heading={heading}
				subText={subText}
				onCardClick={() => setShowEMIPage(true)}
				tag={tag}
			/>
			<Dialog
				fullScreen
				open={showEMIPage}
				onClose={closeWithHash}
				TransitionComponent={Slide}
				TransitionProps={
					{
						direction: 'right',
						timeout: 300,
					} as SlideProps
				}
				classes={{ container: 'scrollable-container pt-header-height' }}
			>
				<Header headerText={heading} subHeaderText={pgFeeText} onBack={closeWithHash} />
				<EMIPage savedCards={savedCards} emiOptions={emiOptions} />
			</Dialog>
		</Fragment>
	);
};

export default EMISectionContainer;
